import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import Input from '../common/Input/Input';
import Table from '../common/Table/Table';

export default function UserCosts() {
  const { apiClient } = useApi();
  const [usersData, setUsersData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default range: Last 30 days
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // Load User List
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.request(`/metrics/users/summary?from_date=${dateRange.from}&to_date=${dateRange.to}`);
      setUsersData(data || []);
      if (data.length > 0) {
        setSelectedUser(prev => prev ?? data[0]);
      }
    } catch (e) {
      console.error(e);
      setUsersData([]);
    }
    setLoading(false);
  }, [dateRange, apiClient]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const exportUserDetail = async () => {
    if (!selectedUser) return;

    try {
      const authToken = localStorage.getItem('cognito_id_token');
      const response = await apiClient.request(
        `/metrics/export/user-detail?user_id=${selectedUser.user_id}&from_date=${dateRange.from}&to_date=${dateRange.to}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-detail-${selectedUser.email}-${dateRange.from}-${dateRange.to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting user detail:', error);
      alert('Failed to export user detail. Please try again.');
    }
  };

  // Load User Detail
  useEffect(() => {
    if (!selectedUser) return;
    const loadDetail = async () => {
      try {
        const data = await apiClient.request(`/metrics/users/${selectedUser.user_id}/detail?from_date=${dateRange.from}&to_date=${dateRange.to}`);
        setUserDetail(data);
      } catch (e) {
        console.error(e);
        setUserDetail(null);
      }
    };
    loadDetail();
  }, [selectedUser, dateRange, apiClient]);

  // Helpers
  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  const formatNumber = (val) => new Intl.NumberFormat('en-US').format(val || 0);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Table Columns
  const modelColumns = [
    {
      header: 'Provider',
      width: '120px',
      render: r => <span className="provider-badge">{r._id.provider}</span>
    },
    {
      header: 'Model',
      accessor: '_id.model',
      width: '200px'
    },
    {
      header: 'Tokens',
      accessor: 'totalTokens',
      width: '120px',
      render: r => formatNumber(r.totalTokens)
    },
    {
      header: 'Cost',
      accessor: 'costUsd',
      width: '120px',
      render: r => <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatCurrency(r.costUsd)}</span>
    },
    {
      header: 'Requests',
      accessor: 'requests',
      width: '100px'
    }
  ];

  return (
    <div className="user-costs-view">
      {/* Filters Toolbar */}
      <div className="metrics-toolbar">
        <div style={{ width: '180px' }}>
          <Input
            type="date"
            label="From"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>
        <div style={{ width: '180px' }}>
          <Input
            type="date"
            label="To"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>
        <div className="toolbar-spacer" style={{ flex: 1 }}></div>

        <button
          className="metrics-btn"
          onClick={exportUserDetail}
          title="Export User Detail"
        >
          <i className="fas fa-file-csv"></i>
          Export Detail
        </button>
      </div>

      <div className="user-costs-content">
        {/* Left: User List */}
        <div className="users-cost-list">
          <div className="list-header">
            <h3>Users by Cost</h3>
          </div>
          <div className="users-list-scroll">
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <i className="fas fa-spinner fa-spin"></i>
                <p style={{ margin: '0.5rem 0 0 0' }}>Loading...</p>
              </div>
            ) : usersData.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <p>No users found</p>
              </div>
            ) : (
              usersData.map((user) => (
                <div
                  key={user.user_id}
                  className={`user-cost-item ${selectedUser?.user_id === user.user_id ? 'selected' : ''}`}
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="user-avatar-small">
                    {user.avatar_url ? <img src={user.avatar_url} alt="" /> : user.display_name?.charAt(0)}
                  </div>
                  <div className="user-item-info">
                    <div className="user-item-name">{user.display_name}</div>
                    <div className="user-item-cost">{formatCurrency(user.total_cost_usd)}</div>
                  </div>
                  <i className="fas fa-chevron-right" style={{ fontSize: '0.8rem', opacity: 0.3 }}></i>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Details Panel */}
        <div className="user-cost-detail">
          {selectedUser && userDetail ? (
            <>
              {/* Header & Stats Strip */}
              <div className="detail-header-group">
                <div className="detail-identity">
                  <h2>{userDetail.userInfo?.displayName || selectedUser.display_name}</h2>
                  <p>{userDetail.userInfo?.email || selectedUser.email}</p>
                </div>
                <div className="kpi-strip">
                  <div className="kpi-item">
                    <span className="kpi-label">Total Cost</span>
                    <span className="kpi-value cost">{formatCurrency(selectedUser.total_cost_usd)}</span>
                  </div>
                  <div className="kpi-item">
                    <span className="kpi-label">Tokens</span>
                    <span className="kpi-value">{formatNumber(selectedUser.total_tokens)}</span>
                  </div>
                  <div className="kpi-item">
                    <span className="kpi-label">Requests</span>
                    <span className="kpi-value">{formatNumber(selectedUser.requests)}</span>
                  </div>
                </div>
              </div>
              {/* Enhanced CSS Chart */}
              {userDetail.dailyCosts && userDetail.dailyCosts.length > 0 && (
                <div className="chart-container">
                  <div className="chart-title">Daily Cost Trend</div>
                  <div className="css-bar-chart">
                    {userDetail.dailyCosts.map((day, idx) => {
                      const maxCost = Math.max(...userDetail.dailyCosts.map(d => d.costUsd), 0.01);
                      const heightPercent = (day.costUsd / maxCost) * 100;

                      // Show date label every 3-5 days to avoid clutter
                      const showLabel = idx % 3 === 0;

                      return (
                        <div key={day._id} className="chart-column">
                          <div
                            className="chart-bar-visual"
                            style={{ height: `${Math.max(2, heightPercent)}%` }}
                          ></div>
                          <div className="chart-tooltip">
                            {formatDate(day._id)}: {formatCurrency(day.costUsd)}
                          </div>
                          {showLabel && <div className="chart-label">{formatDate(day._id)}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Table Section */}
              <div>
                <div className="chart-title" style={{ marginBottom: '1rem' }}>Model Usage Breakdown</div>
                <Table
                  columns={modelColumns}
                  data={userDetail.modelsBreakdown || []}
                  emptyMessage="No model usage data available"
                />
              </div>
            </>
          ) : (
            <div className="detail-empty">
              <i className="fas fa-chart-pie"></i>
              <h3>Select a User</h3>
              <p>Select a user from the list to view detailed cost analysis.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
